import React from "react";
import {connect} from "react-redux";
import PercentIcon from "@mui/icons-material/Percent";

import DiscountCommon from "./discountCommon";
import {DISCOUNT_TYPES} from "./discounstInterface";
import {setDiscountSections, setFinanceActiveDiscount} from "../../actions/priceWorksheetActions";
import {getDiscountCurrentValue, shouldUseMultipleWorkSheets} from "./discountFunctions";
import TextInput from "../ui/textInput";
import type {RootState} from "../../storeHooks";

interface DiscountAddFeeState {
  percentEditing: boolean;
  percentValue: string;
}

/**
 * addFee discount - unlike other discounts, its amount is ADDED to the total.
 * - type Price: works like freeFill (custom amount input), but additive.
 * - type Percent: `amount` stores the percentage. Not editing - the input shows the calculated price
 *   (percent of the subtotal before this discount, so it follows total changes). On focus - it shows the percentage,
 *   on blur the new percentage is saved to `amount`.
 */
class DiscountAddFee extends DiscountCommon {

  state: DiscountAddFeeState = {
    percentEditing: false,
    percentValue: "",
  };

  getRealValue = (property: string) => {
    const {discount, activeQuoteIndex, hasMultipleWorkSheets} = this.props;
    return getDiscountCurrentValue(discount, property, activeQuoteIndex, hasMultipleWorkSheets);
  };

  handlePercentFocus = () => {
    const realAmount = +this.getRealValue("amount") || 0;
    this.setState({
      percentEditing: true,
      percentValue: realAmount ? String(realAmount) : "",
    });
  };

  handlePercentRealChange = (value: string) => {
    this.setState({percentValue: value});
  };

  /**
   * called by TextInput after blur with a parsed number - saves the percentage as the discount amount
   */
  handlePercentFinish = (percent: number) => {
    this.setState({percentEditing: false});
    if ((+percent || 0) !== (+this.getRealValue("amount") || 0)) {
      this.setDiscountProperty("amount", +percent || 0);
    }
  };

  renderPercentInput = () => {
    const {percentEditing, percentValue} = this.state;
    return (
      <React.Fragment>
        <TextInput className={"ui-input discount-special__amount discount-cell--active-color price-input"
          + (percentEditing ? " ui-input--percentage" : "")}
                   numberInput={true}
                   autoComplete={"off"}
                   usePropsValue={true}
                   onFocus={this.handlePercentFocus}
                   onRealChange={this.handlePercentRealChange}
                   onChange={this.handlePercentFinish}
                   value={percentEditing ? percentValue : this.getInputValue()}/>
        {percentEditing ? <PercentIcon className="color-active"/> : null}
      </React.Fragment>
    );
  };

  render() {
    const {discount} = this.props;
    const disabled = this.isDiscountDisabled();
    const isPercentInput = this.getRealValue("type") === DISCOUNT_TYPES.percent;

    return (
      <li className={"discount" + (discount.selected ? " discount--special" : "")
        + (disabled ? " discount--disabled" : "")}>
        <div className="discount-cell discount-cell--select">
          <button className={"ui-checkbox" + (discount.selected ? " ui-checkbox--checked" : "")}
                  onClick={this.handleDiscountSelectClick}/>
        </div>
        <div className="discount-cell discount-cell--special-name">
          {discount.selected
            ? <input className="ui-input discount-special__name"
                     value={discount.name}
                     onChange={(event) => this.setDiscountProperty("name", event.target.value)}/>
            : null}
        </div>
        <div className="discount-cell discount-cell--special-amount">
          {discount.selected
            ? (isPercentInput ? this.renderPercentInput() : this.renderAmountInput())
            : null}
        </div>
      </li>
    );
  }
}

const mapStateToProps = (store: RootState) => {
  const {discountSections, customAddedDiscounts, workSheetsCount, financeActiveDiscount} = store.priceWorksheetState;
  const {allWizardsValues} = store.wizardState;
  const {activeQuoteIndex, quotes} = store.quotesState;
  const {jobIsSaving} = store.serverState;
  const {showAllQuotesCartItemsList} = store.cartState;

  return {
    discountSections,
    customAddedDiscounts,
    // Total requirements
    cartItems: store.cartState.cartItems,
    instList: store.instState.instList,
    fabricatorData: store.fabricatorState,
    fabricatorTaxes: store.fabricatorState.fabricatorTaxes,
    fabricatorSettings: store.fabricatorState.settings,
    // Total requirements end
    allWizardsValues,
    activeQuoteIndex,
    workSheetsCount,
    quotes,
    hasMultipleWorkSheets: shouldUseMultipleWorkSheets(workSheetsCount),
    jobIsSaving,
    financeActiveDiscount,
    showAllQuotesCartItemsList,
  };
};

const mapDispatchToProps = {
  setDiscountSections,
  setFinanceActiveDiscount,
};

export default connect(mapStateToProps, mapDispatchToProps)(DiscountAddFee);
